import { useState, useMemo, useEffect } from 'react';
import { format, addDays, startOfDay, parseISO } from 'date-fns';
import { SEARCH_TYPES, SEARCH_COUNTS_LIMITS } from '../constants';
import { useSearchParams } from 'react-router-dom';

const { ROUNDTRIP, ONEWAY, MULTICITY } = SEARCH_TYPES;

const NOW = new Date();
const TODAY = format(addDays(NOW, 0), 'yyyy-MM-dd');

const useSearchState = ({ status, handleSearch, params, currency, sortBy, onSearchChange }, departureObj, destinationObj) => {
  const [searchParams] = useSearchParams();

  const departureDateFromUrl = searchParams.get('departureDate');
  const returnDateDateFromUrl = searchParams.get('returnDate');
  const tripTypeFromUrl = searchParams.get('tripType');

  const [bookingRequest, setBookingRequest] = useState({
    tripType: tripTypeFromUrl || ONEWAY,
    departureStation: '',
    destinationStation: '',
    departureDate: departureDateFromUrl != null ? parseISO(departureDateFromUrl) : parseISO(TODAY),
    returnDate: returnDateDateFromUrl != null ? parseISO(returnDateDateFromUrl) : null,
    cabinClass: null,
    adults: 1,
    children: 0,
    infants: 0,
    hold: 1,
    carryOn: 0,
  });

  useEffect(() => {
    setBookingRequest({
      ...bookingRequest,
      departureStation: departureObj,
      destinationStation: destinationObj,
    });
  }, [destinationObj, departureObj]);

  const [slices, updateSlices] = useState([]);
  const [errors, setErrors] = useState({});
  const [initFetched, setInitFetched] = useState(false);

  const validate = (key, value) => {

    let valid = true;

    if (key === 'departureDate') {
      const date = new Date(startOfDay(parseISO(value)));
      valid = !(date < startOfDay(NOW));

      if (!valid) setErrors({ ...errors, departureDate: 'الرجاء إدخال تاريخ صالح للمغادرة' });
    }

    if (key === 'returnDate') {
      valid = !(bookingRequest.tripType === ROUNDTRIP &&
        new Date(value) < new Date(bookingRequest.departureDate));

      if (!valid) setErrors({ ...errors, returnDate: 'الرجاء إدخال تاريخ صالح للعودة' });
    }

    if (valid) setErrors({ ...errors, [key]: null });
  };

  const setInitSlices = () => {
    updateSlices([{
      id: 0,
      departureStation: bookingRequest.departureStation,
      destinationStation: bookingRequest.destinationStation,
      departureDate: bookingRequest.departureDate
    },
    {
      id: 1,
      departureStation: null,
      destinationStation: null,
      departureDate: parseISO(TODAY),
    },
    ]);
  };

  const handleSearchChange = (key, value) => {
    if (typeof onSearchChange === 'function') {
      if (['adults', 'children', 'infants'].includes(key)) return onSearchChange('passengers', { [key]: value });
      if (['hold', 'carryOn'].includes(key)) return onSearchChange('baggage', { [key]: value });
      if (['departureStation', 'destinationStation', 'slices', 'departureDate','returnDate', 'cabinClass'].includes(key)) return onSearchChange(key, value);
    }
  };

  const update = (key, value) => {
    setBookingRequest({ ...bookingRequest, [key]: value });

    validate(key, value);

    if (key === 'tripType' && value === MULTICITY && slices.length < 2) setInitSlices();

    handleSearchChange(key, value);
  };

  useEffect(() => {
    const updateUrl = () => {
      window.history.pushState(null, '', `?tripType=${bookingRequest.tripType ? bookingRequest.tripType : ''}&departureStation=${bookingRequest.departureStation != undefined ? bookingRequest.departureStation?.iataCode : ''}&destinationStation=${bookingRequest.destinationStation != undefined ? bookingRequest.destinationStation?.iataCode : ''}&departureDate=${bookingRequest.departureDate != undefined ? format(bookingRequest.departureDate, 'yyyy-MM-dd') : ''}${bookingRequest.tripType == 'round-trip' && bookingRequest.returnDate != undefined ? `&returnDate=${format(bookingRequest.returnDate, 'yyyy-MM-dd')}` : ''}`);
    };
    updateUrl();
  }, [bookingRequest]);
  const increase = (key) => {
    const value = bookingRequest[key] + 1;
    if (bookingRequest[key] < SEARCH_COUNTS_LIMITS[key]) update(key, value);
  };

  const decrease = (key) => {
    if (bookingRequest[key] > 0) {
      const value = bookingRequest[key] - 1;
      update(key, value);
    }
  };

  const search = (e) => {
    e.preventDefault();
    handleSearch({ ...bookingRequest, slices });
  };

  const switchDirection = () => {
    setBookingRequest({
      ...bookingRequest,
      departureStation: bookingRequest.destinationStation,
      destinationStation: bookingRequest.departureStation
    });
  };

  const isBasicInfoSet = bookingRequest.departureDate
    && bookingRequest.departureStation?.iataCode
    && bookingRequest.destinationStation?.iataCode;

  const onWayCondition = () => bookingRequest.tripType === ONEWAY && isBasicInfoSet;
  const roundTripCondition = () => bookingRequest.tripType === ROUNDTRIP && bookingRequest.returnDate && isBasicInfoSet;
  const multiCityCondition = () => bookingRequest.tripType === MULTICITY && slices?.every(slice => slice.departureDate
    && slice.departureStation?.iataCode
    && slice.destinationStation?.iataCode
  );

  const readyToSubmit = useMemo(() =>
    (onWayCondition() || roundTripCondition() || multiCityCondition())
    && (bookingRequest.adults || bookingRequest.children), [slices, bookingRequest]);

  useEffect(() => {
    if (!initFetched && params && !bookingRequest.departureStation) {
      setInitFetched(true);
      setBookingRequest(params);
      updateSlices(params.slices);
      handleSearch(params);

      onSearchChange('passengers', { adults: params.adults, children: params.children, infants: params.infants });
      onSearchChange('baggage', { hold: params.hold, carryOn: params.carryOn });
    }
  }, [params, initFetched]);

  useEffect(() => {
    if (currency) {
      handleSearch({
        ...bookingRequest,
        slices,
        currency
      });
    }
  }, [currency]);

  useEffect(() => {
    if (sortBy) {
      handleSearch({ ...bookingRequest, slices, order: sortBy });
    }
  }, [sortBy]);

  useEffect(() => {
    if (status === 'should-fetch-more') handleSearch({ ...bookingRequest, slices, currency });
  }, [status]);

  return {
    data: { bookingRequest, slices, errors, readyToSubmit },
    actions: {
      update,
      increase,
      decrease,
      switchDirection,
      search,
      updateSlices
    }
  };
};

export default useSearchState;